import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import TcnApp from '../../../components/tcn/TcnApp';
import TopBar from '../../../components/tcn/layout/TopBar';
import { Card, AccountAvatar, SectionLabel, Icon } from '../../../components/tcn/ui-kit';
import { TCN } from '../../../components/tcn/ui-kit/tokens';
import AlertBell from '../../../components/tcn-modules/m7/AlertBell';
import PayoutHeadline from '../../../components/tcn-modules/m5/PayoutHeadline';
import BonusCard from '../../../components/tcn-modules/m5/BonusCard';
import BonusDetail from '../../../components/tcn-modules/m5/BonusDetail';
import BonusGapActions from '../../../components/tcn-modules/m5/BonusGapActions';
import GenerateActionsSheet from '../../../components/tcn-modules/m5/GenerateActionsSheet';
import { formatInt, formatPct } from '../../../components/tcn-modules/shared/format';
import useIsDesktop from '../../../hooks/useIsDesktop';

const BONUS_KINDS = ['rankup', 'activeness', 'revenue', 'recruitment'];
const BONUS_TITLE = {
  rankup: 'Rank-Up bonus', activeness: 'Activeness bonus',
  revenue: 'Revenue bonus', recruitment: 'Recruitment bonus',
};

/**
 * /tcn/me/payout — Module 5 — My Pay-Out.
 *
 * Single SELECT on `manager_current_month` filtered by manager_id = auth.uid().
 * Reads the *_clear columns (RLS blocks super_admin, mirrors *_enc decrypted
 * by the recompute — Phase A). Reads diamond_to_usd_rate from metric_thresholds
 * (server=MENA per R7, value 0.01 — D-040).
 *
 * Layout:
 *   - PayoutHeadline (total earned/potential/gap)
 *   - 4 BonusCards: Rank-Up · Activeness · Revenue · Recruitment
 *   - BonusGapActions (biggest_bonus_gap_component + bonus_action_list_jsonb)
 *
 * Mobile  → cards stacked vertically · Desktop → 2x2 grid for bonuses
 */
const TcnPayoutPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const isDesktop = useIsDesktop();
  const [searchParams, setSearchParams] = useSearchParams();
  const bonus = BONUS_KINDS.includes(searchParams.get('bonus')) ? searchParams.get('bonus') : null;
  const openBonus = (k) => setSearchParams((p) => { const n = new URLSearchParams(p); n.set('bonus', k); return n; });
  const closeBonus = () => setSearchParams((p) => { const n = new URLSearchParams(p); n.delete('bonus'); return n; });
  const [data, setData] = useState(null);
  const [activenessRatios, setActivenessRatios] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usdRate, setUsdRate] = useState(0.01);
  const [generateOpen, setGenerateOpen] = useState(false);

  const userName = userProfile?.full_name || userProfile?.display_name || userProfile?.email || 'Agent';

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    const [{ data: row, error: err }, { data: rateRow }, { data: actCfg }] = await Promise.all([
      supabase
        .from('manager_current_month')
        .select('*')
        .eq('manager_id', user.id)
        .maybeSingle(),
      supabase
        .from('metric_thresholds')
        .select('target_value')
        .eq('metric_name', 'diamond_to_usd_rate')
        .eq('server', 'MENA')
        .maybeSingle(),
      // Activeness bonus ratios per level → A5.3 detail (MENA, R7).
      supabase.from('activeness_config').select('level, bonus_ratio').eq('server', 'MENA'),
    ]);
    if (err && err.code !== 'PGRST116') setError(err);
    else setData(row || null);
    if (rateRow?.target_value != null) {
      const n = Number(rateRow.target_value);
      if (!Number.isNaN(n) && n > 0) setUsdRate(n);
    }
    if (actCfg) setActivenessRatios(Object.fromEntries(actCfg.map((r) => [r.level, Number(r.bonus_ratio)])));
    setLoading(false);
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  const handleTabChange = (tab) => {
    switch (tab) {
      case 'today':    return navigate('/tcn/me');
      case 'creators': return navigate('/tcn/me/creators');
      case 'tiers':    return navigate('/tcn/me/tiers');
      case 'payout':   return navigate('/tcn/me/payout');
      case 'actions':  return navigate('/tcn/me/actions');
      default:         return navigate('/tcn/me');
    }
  };

  // Derive USD amounts from *_clear columns.
  const usd = (clear) => clear == null ? null : Number(clear) * usdRate;

  // 4 bonus cards data
  const bonuses = data ? [
    {
      kind: 'rankup',
      earnedUsd: usd(data.rankup_bonus_earned_clear),
      potentialUsd: usd(data.rankup_bonus_potential_clear),
      secondary: [
        { label: t('tcn.m5.bonus.rankup.upgrades', 'Upgrades'), value: formatInt(data.upgrade_count) },
        { label: t('tcn.m5.bonus.rankup.maintain', 'Maintain'), value: formatInt(data.maintain_count) },
      ],
    },
    {
      kind: 'activeness',
      earnedUsd: usd(data.activeness_bonus_earned_clear),
      potentialUsd: usd(data.activeness_bonus_potential_clear),
      secondary: [
        { label: 'L5', value: formatInt(data.creators_at_level_5) },
        { label: 'L4', value: formatInt(data.creators_at_level_4) },
        { label: 'L3', value: formatInt(data.creators_at_level_3) },
      ],
    },
    {
      kind: 'revenue',
      earnedUsd: usd(data.revenue_bonus_current_clear),
      potentialUsd: usd(data.revenue_bonus_full_threshold_clear),
      secondary: [
        { label: t('tcn.m5.bonus.revenue.palier', 'Palier'), value: formatPct(data.revenue_bonus_current_palier_pct) },
        { label: t('tcn.m5.bonus.revenue.bonusPct', 'Bonus %'), value: formatPct(data.revenue_bonus_current_bonus_pct, { decimals: 1 }) },
      ],
    },
    {
      kind: 'recruitment',
      earnedUsd: usd(data.recruitment_bonus_current_clear),
      // Potential = top palier (recruitment_bonus_config, super-admin managed).
      potentialUsd: usd(data.recruitment_bonus_potential_clear ?? data.recruitment_bonus_current_clear),
      secondary: [
        { label: t('tcn.m5.bonus.recruitment.recruits', 'Recruits'), value: formatInt(data.recruitment_mtd_count) },
        { label: 'T+14', value: formatInt(data.recruitment_t14_count) },
        { label: 'T+30 %', value: formatPct(data.recruitment_t30_rate) },
      ],
    },
  ] : [];

  const biggestGapAmount = data?.biggest_bonus_gap_amount_clear != null
    ? Number(data.biggest_bonus_gap_amount_clear) * usdRate
    : 0;
  const actionList = Array.isArray(data?.bonus_action_list_jsonb) ? data.bonus_action_list_jsonb : [];

  // A5.7 — auto-generate one coaching action per bonus component that still has
  // unrealised upside (potential > earned). Each carries the $ it unlocks; the
  // sheet lets the agent tick a subset and "Save all" into the Action Log.
  const GEN_COPY = {
    rankup: {
      title: t('tcn.m5.generate.rankup.title', 'Coach eligible creators to rank up'),
      description: t('tcn.m5.generate.rankup.desc', '{{n}} upgraded so far — push the 7D/15H-eligible toward their tier target', {
        n: formatInt(data?.upgrade_count || 0),
      }),
    },
    activeness: {
      title: t('tcn.m5.generate.activeness.title', 'Lift creators to a higher activeness level'),
      description: t('tcn.m5.generate.activeness.desc', 'More LIVE days & hours unlock the next per-creator activeness bonus'),
    },
    revenue: {
      title: t('tcn.m5.generate.revenue.title', 'Reach the next revenue palier'),
      description: t('tcn.m5.generate.revenue.desc', 'Close the completion gap to cross into the next bonus threshold'),
    },
    recruitment: {
      title: t('tcn.m5.generate.recruitment.title', 'Onboard more creators this month'),
      description: t('tcn.m5.generate.recruitment.desc', '{{n}} recruited — reach the next recruitment palier', {
        n: formatInt(data?.recruitment_mtd_count || 0),
      }),
    },
  };
  const generatedActions = bonuses
    .map((b) => {
      const upside = (b.potentialUsd || 0) - (b.earnedUsd || 0);
      return upside > 0.5
        ? { key: b.kind, component: b.kind, upsideUsd: upside, ...GEN_COPY[b.kind] }
        : null;
    })
    .filter(Boolean)
    .sort((a, z) => z.upsideUsd - a.upsideUsd);
  const gapComponentLabel = data?.biggest_bonus_gap_component
    ? t(`tcn.m5.bonus.${data.biggest_bonus_gap_component}.label`, data.biggest_bonus_gap_component)
    : '';

  const handleSaveAll = async (selected) => {
    if (!user?.id || !data || selected.length === 0) return;
    const today = new Date().toISOString().slice(0, 10);
    const rows = selected.map((a) => ({
      tenant_id: data.tenant_id,
      agency_id: data.agency_id,
      agent_id: user.id,
      source_activity: 'bonus_gap',
      title: a.title,
      description: a.description,
      due_date: today,
      status: 'open',
    }));
    const { error: insErr } = await supabase.from('action_log').insert(rows);
    if (insErr) throw insErr;
  };

  return (
    <TcnApp
      tab="payout"
      onTabChange={handleTabChange}
      desktopUserName={userName}
      desktopBreadcrumb={[
        t('tcn.m1.breadcrumb.dashboard', 'Dashboard'),
        t('tcn.tabs.payout', 'Pay-out'),
      ]}
      mobileHeader={
        bonus ? (
          <TopBar
            leading={
              <button type="button" onClick={closeBonus} aria-label={t('tcn.common.back', 'Back')}
                style={{ background: 'transparent', border: 'none', color: TCN.cyan, cursor: 'pointer', padding: 4 }}>
                <Icon name="chevron-left" size={20} />
              </button>
            }
            title={t(`tcn.m5.bonus.${bonus}.label`, BONUS_TITLE[bonus])}
            subtitle={t('tcn.m5.detail.subtitle', 'Bonus breakdown')}
            largeTitle
          />
        ) : (
          <TopBar
            leading={<AccountAvatar name={userName} size={36} ring />}
            trailing={<AlertBell />}
            title={t('tcn.m5.title', 'My pay-out')}
            subtitle={t('tcn.m5.subtitle', 'Bonus components & gap')}
            largeTitle
          />
        )
      }
    >
      <div
        style={{
          maxWidth: isDesktop ? 1200 : '100%',
          padding: isDesktop ? 0 : '0 16px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        {loading && (
          <Card padded style={{ padding: 16, color: TCN.textDim, fontSize: 13 }}>
            {t('tcn.common.loading', 'Loading…')}
          </Card>
        )}
        {error && (
          <Card padded style={{ padding: 16, color: TCN.red, fontSize: 13 }}>
            {error.message}
          </Card>
        )}

        {!loading && !error && !data && (
          <Card padded style={{ padding: 16, color: TCN.textDim, fontSize: 13 }}>
            {t('tcn.m5.empty', 'No payout data yet. Run an injection to populate this view.')}
          </Card>
        )}

        {data && bonus && (
          <BonusDetail kind={bonus} data={data} usdRate={usdRate} activenessRatios={activenessRatios} />
        )}

        {data && !bonus && (
          <>
            <PayoutHeadline data={data} usdRate={usdRate} />

            <SectionLabel>{t('tcn.m5.section.bonuses', 'Bonus components')}</SectionLabel>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isDesktop ? 'repeat(2, 1fr)' : '1fr',
                gap: 12,
              }}
            >
              {bonuses.map((b) => (
                <BonusCard
                  key={b.kind}
                  kind={b.kind}
                  earnedUsd={b.earnedUsd}
                  potentialUsd={b.potentialUsd}
                  secondary={b.secondary}
                  highlight={data.biggest_bonus_gap_component === b.kind}
                  onClick={() => openBonus(b.kind)}
                />
              ))}
            </div>

            {data.biggest_bonus_gap_component && (
              <BonusGapActions
                component={data.biggest_bonus_gap_component}
                amountUsd={biggestGapAmount}
                actions={actionList}
                onGenerate={generatedActions.length > 0 ? () => setGenerateOpen(true) : undefined}
              />
            )}
          </>
        )}
      </div>

      <GenerateActionsSheet
        open={generateOpen}
        onClose={() => setGenerateOpen(false)}
        gapUsd={biggestGapAmount}
        gapComponentLabel={gapComponentLabel}
        actions={generatedActions}
        onSaveAll={handleSaveAll}
      />
    </TcnApp>
  );
};

export default TcnPayoutPage;
